import React from 'react';
import { Card, Checkbox, Space, Tag, Tooltip, Typography } from 'antd';
import { Link } from 'react-router-dom';
import type { DeliveryReadinessRow } from '@/services/delivery.service';
import { WorkOrderExecutionBadge } from '@/components/manufacturing/WorkOrderExecutionBadge';

const { Text } = Typography;

const statusColors: Record<string, string> = {
  ready: 'success',
  partial: 'processing',
  blocked: 'error',
  delivered: 'default',
};

interface DeliveryReadinessCardProps {
  row: DeliveryReadinessRow;
  selectable?: boolean;
  selected?: boolean;
  onSelectChange?: (row: DeliveryReadinessRow, checked: boolean) => void;
}

export const DeliveryReadinessCard: React.FC<DeliveryReadinessCardProps> = ({
  row,
  selectable,
  selected,
  onSelectChange,
}) => (
  <Card
    size="small"
    style={{ height: '100%' }}
    title={
      <Space wrap>
        {selectable && (
          <Checkbox
            checked={selected}
            onChange={(e) => onSelectChange?.(row, e.target.checked)}
          />
        )}
        <Tag color={statusColors[row.readiness_status] ?? 'default'}>{row.readiness_status}</Tag>
        {row.execution_scope && (
          <WorkOrderExecutionBadge executionScope={row.execution_scope} size="compact" />
        )}
      </Space>
    }
  >
    <Space direction="vertical" size={4} style={{ width: '100%' }}>
      <Text strong>{row.customer_name ?? '—'}</Text>
      <Text type="secondary">
        {row.sales_order_number ? (
          <Link to={`/growth/orders/${row.sales_order_id}`}>{row.sales_order_number}</Link>
        ) : (
          'No customer order'
        )}{' '}
        ·{' '}
        <Link to={`/mfg/work-orders/${row.work_order_id}`}>{row.work_order_number}</Link>
      </Text>
      {row.project_name && <Text type="secondary">Project: {row.project_name}</Text>}
      {row.item_code && (
        <Text>
          Item code: <Text strong copyable>{row.item_code}</Text>
        </Text>
      )}
      <Text>
        {row.item_description}
        {row.production_unit_number != null ? ` · Unit #${row.production_unit_number}` : ''}
      </Text>
      <Text>
        Available: <Text strong>{row.available_quantity}</Text>
        {row.units_ready != null && row.units_planned != null && (
          <Text type="secondary"> ({row.units_ready} of {row.units_planned} units)</Text>
        )}
      </Text>
      {row.fir_status && (
        <Text type="secondary">FIR: {row.fir_status}</Text>
      )}
      {row.target_delivery_date && (
        <Text type="secondary">
          Target: {row.target_delivery_date}
          {row.days_overdue != null && row.days_overdue > 0 && (
            <Tag color="warning" style={{ marginLeft: 8 }}>
              {row.days_overdue}d overdue
            </Tag>
          )}
        </Text>
      )}
      {row.delivery_location && (
        <Tooltip title={row.delivery_location}>
          <Text type="secondary" ellipsis>
            Ship to: {row.delivery_location}
          </Text>
        </Tooltip>
      )}
      {row.reasons.length > 0 && (
        <Tooltip title={row.reasons.join(' · ')}>
          <Text type="danger" ellipsis>
            {row.reasons.join(' · ')}
          </Text>
        </Tooltip>
      )}
    </Space>
  </Card>
);
